import React, { memo } from 'react';
import { ClipboardCheckIcon, BookOpenIcon, TrophyIcon, CheckIcon, LockIcon, ShieldIcon, WifiIcon, SmartphoneIcon, CreditCardIcon, EyeOffIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TopBar } from '../components/TopBar';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
export function HowItWorksPage() {
  return <div className="flex flex-col min-h-screen w-full" data-id="element-1442">
      <TopBar data-id="element-1443" />
      <Header data-id="element-1444" />

      {/* Hero Section */}
      <section className="w-full bg-white py-20 md:py-28 border-b border-gray-200" data-id="element-1445">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1446">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1447">
            <h1 className="mb-6 text-5xl md:text-6xl font-extrabold text-[#111111] leading-tight" data-id="element-1448">
              How Caveo Works
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 leading-relaxed" data-id="element-1449">
              A simple 3-level education system that builds real digital
              confidence—at your own pace, in plain English.
            </p>
          </div>
        </div>
      </section>

      {/* The 3-Step Journey */}
      <section className="w-full bg-[#f8f8f8] py-16 md:py-24" data-id="element-1450">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1451">
          <div className="text-center mb-16" data-id="element-1452">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-4" data-id="element-1453">
              Your journey to digital confidence
            </h2>
            <p className="text-lg text-gray-600" data-id="element-1454">
              Three simple steps. No tech degree required.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-1455">
            <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 text-center relative" data-id="element-1456">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#631bb7] text-white w-10 h-10 rounded-full flex items-center justify-center font-bold" data-id="element-1457">
                1
              </div>
              <div className="w-20 h-20 bg-[#631bb7] bg-opacity-5 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-[#631bb7] border-opacity-10" data-id="element-1458">
                <ClipboardCheckIcon className="w-10 h-10 text-[#631bb7]" data-id="element-1459" />
              </div>
              <h3 className="text-2xl font-bold text-[#111111] mb-4" data-id="element-1460">Assess</h3>
              <p className="text-gray-600 leading-relaxed mb-4" data-id="element-1461">
                Answer simple questions about your devices, habits, and
                concerns. In under 3 minutes, we'll create a personalized
                security plan.
              </p>
              <ul className="text-left space-y-2" data-id="element-1462">
                <li className="flex items-center text-sm text-gray-500" data-id="element-1463">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1464" />
                  No technical knowledge needed
                </li>
                <li className="flex items-center text-sm text-gray-500" data-id="element-1465">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1466" />
                  Skip questions that don't apply
                </li>
                <li className="flex items-center text-sm text-gray-500" data-id="element-1467">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1468" />
                  Get your Security Score instantly
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 text-center relative" data-id="element-1469">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#631bb7] text-white w-10 h-10 rounded-full flex items-center justify-center font-bold" data-id="element-1470">
                2
              </div>
              <div className="w-20 h-20 bg-[#631bb7] bg-opacity-5 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-[#631bb7] border-opacity-10" data-id="element-1471">
                <BookOpenIcon className="w-10 h-10 text-[#631bb7]" data-id="element-1472" />
              </div>
              <h3 className="text-2xl font-bold text-[#111111] mb-4" data-id="element-1473">Guide</h3>
              <p className="text-gray-600 leading-relaxed mb-4" data-id="element-1474">
                Follow bite-sized lessons with clear visuals and step-by-step
                instructions. Learn at your own pace—no pressure.
              </p>
              <ul className="text-left space-y-2" data-id="element-1475">
                <li className="flex items-center text-sm text-gray-500" data-id="element-1476">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1477" />
                  Plain English, no jargon
                </li>
                <li className="flex items-center text-sm text-gray-500" data-id="element-1478">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1479" />
                  Visual guides show exactly what to do
                </li>
                <li className="flex items-center text-sm text-gray-500" data-id="element-1480">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1481" />
                  Pause and resume anytime
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 text-center relative" data-id="element-1482">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#631bb7] text-white w-10 h-10 rounded-full flex items-center justify-center font-bold" data-id="element-1483">
                3
              </div>
              <div className="w-20 h-20 bg-[#631bb7] bg-opacity-5 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-[#631bb7] border-opacity-10" data-id="element-1484">
                <TrophyIcon className="w-10 h-10 text-[#631bb7]" data-id="element-1485" />
              </div>
              <h3 className="text-2xl font-bold text-[#111111] mb-4" data-id="element-1486">
                Empower
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4" data-id="element-1487">
                Track your progress, celebrate wins, and watch your confidence
                grow. See your Security Score improve over time.
              </p>
              <ul className="text-left space-y-2" data-id="element-1488">
                <li className="flex items-center text-sm text-gray-500" data-id="element-1489">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1490" />
                  Family dashboard shows everyone's progress
                </li>
                <li className="flex items-center text-sm text-gray-500" data-id="element-1491">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1492" />
                  Celebrate milestones together
                </li>
                <li className="flex items-center text-sm text-gray-500" data-id="element-1493">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1494" />
                  Build lasting independence
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* The 3-Level Education System */}
      <section className="w-full bg-white py-16 md:py-24 border-t border-gray-200" data-id="element-1495">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1496">
          <div className="text-center mb-16" data-id="element-1497">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-4" data-id="element-1498">
              Our 3-Level Education System
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-1499">
              39 modules across three skill levels. Start where you are, grow at
              your pace.
            </p>
          </div>

          {/* Level 1 */}
          <div className="mb-12" data-id="element-1500">
            <div className="flex items-center mb-6" data-id="element-1501">
              <div className="bg-green-100 text-green-700 px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide mr-4" data-id="element-1502">
                Level 1
              </div>
              <h3 className="text-2xl font-bold text-[#111111]" data-id="element-1503">Essentials</h3>
              <span className="ml-auto text-gray-500 font-medium" data-id="element-1504">
                9 Modules
              </span>
            </div>
            <p className="text-gray-600 mb-6" data-id="element-1505">
              The fundamentals everyone needs. Perfect for beginners or anyone
              wanting to solidify their basics.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1506">
              {[{
              icon: LockIcon,
              title: 'Creating Bulletproof Passwords',
              desc: 'Learn to create and manage strong passwords without memorizing them all'
            }, {
              icon: ShieldIcon,
              title: 'Recognizing Scams & Phishing',
              desc: 'Spot fake emails, texts, and calls before they trick you'
            }, {
              icon: WifiIcon,
              title: 'Securing Your Home WiFi',
              desc: 'Protect your network from unwanted visitors'
            }].map((module, index) => <div key={index} className="bg-[#f8f8f8] rounded-xl p-5 border-2 border-gray-200" data-id="element-1507">
                  <module.icon className="w-6 h-6 text-green-600 mb-3" data-id="element-1508" />
                  <h4 className="font-bold text-[#111111] mb-2" data-id="element-1509">
                    {module.title}
                  </h4>
                  <p className="text-sm text-gray-600" data-id="element-1510">{module.desc}</p>
                </div>)}
            </div>
          </div>

          {/* Level 2 */}
          <div className="mb-12" data-id="element-1511">
            <div className="flex items-center mb-6" data-id="element-1512">
              <div className="bg-blue-100 text-blue-700 px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide mr-4" data-id="element-1513">
                Level 2
              </div>
              <h3 className="text-2xl font-bold text-[#111111]" data-id="element-1514">
                Intermediate
              </h3>
              <span className="ml-auto text-gray-500 font-medium" data-id="element-1515">
                11 Modules
              </span>
            </div>
            <p className="text-gray-600 mb-6" data-id="element-1516">
              Build on your foundation with more advanced protection strategies.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1517">
              {[{
              icon: CreditCardIcon,
              title: 'Protecting Financial Accounts',
              desc: 'Secure your bank accounts, credit cards, and investment apps'
            }, {
              icon: SmartphoneIcon,
              title: 'Mobile Device Security',
              desc: 'Keep your phone and tablet safe from threats'
            }, {
              icon: EyeOffIcon,
              title: 'Privacy Settings Deep Dive',
              desc: 'Take control of what apps and websites know about you'
            }].map((module, index) => <div key={index} className="bg-[#f8f8f8] rounded-xl p-5 border-2 border-gray-200" data-id="element-1518">
                  <module.icon className="w-6 h-6 text-blue-600 mb-3" data-id="element-1519" />
                  <h4 className="font-bold text-[#111111] mb-2" data-id="element-1520">
                    {module.title}
                  </h4>
                  <p className="text-sm text-gray-600" data-id="element-1521">{module.desc}</p>
                </div>)}
            </div>
          </div>

          {/* Level 3 */}
          <div data-id="element-1522">
            <div className="flex items-center mb-6" data-id="element-1523">
              <div className="bg-[#631bb7] bg-opacity-20 text-[#631bb7] px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide mr-4" data-id="element-1524">
                Level 3
              </div>
              <h3 className="text-2xl font-bold text-[#111111]" data-id="element-1525">
                Specialized Guides
              </h3>
              <span className="ml-auto text-gray-500 font-medium" data-id="element-1526">
                19 Deep Dives
              </span>
            </div>
            <p className="text-gray-600 mb-6" data-id="element-1527">
              Expert-level topics for those who want comprehensive protection.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-1528">
              {['AI Safety Primer', 'Digital Legacy Planning', 'Identity Masking', 'Cloud Storage Security', 'Smart Home Protection', 'Travel Security', 'Social Media Privacy', 'Data Broker Removal'].map((topic, index) => <div key={index} className="bg-[#f8f8f8] rounded-lg p-4 border border-gray-200 text-center" data-id="element-1529">
                  <span className="text-sm font-medium text-[#111111]" data-id="element-1530">
                    {topic}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Sample Module Preview */}
      <section className="w-full bg-[#f8f8f8] py-16 md:py-24 border-t border-gray-200" data-id="element-1531">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1532">
          <div className="text-center mb-12" data-id="element-1533">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-4" data-id="element-1534">
              See what a lesson looks like
            </h2>
            <p className="text-lg text-gray-600" data-id="element-1535">
              Clear instructions. Visual guides. No confusion.
            </p>
          </div>

          <div className="max-w-4xl mx-auto bg-white rounded-2xl border-2 border-gray-200 overflow-hidden" data-id="element-1536">
            {/* Module Header */}
            <div className="bg-[#1a0647] p-6" data-id="element-1537">
              <div className="flex items-center justify-between" data-id="element-1538">
                <div data-id="element-1539">
                  <span className="text-[#00f4f8] text-sm font-semibold" data-id="element-1540">
                    Level 1 • Module 2
                  </span>
                  <h3 className="text-white text-xl font-bold mt-1" data-id="element-1541">
                    Recognizing Scams & Phishing
                  </h3>
                </div>
                <div className="text-white text-right" data-id="element-1542">
                  <span className="text-sm opacity-70" data-id="element-1543">Progress</span>
                  <div className="text-lg font-bold" data-id="element-1544">3 of 8 steps</div>
                </div>
              </div>
              <div className="mt-4 bg-white bg-opacity-20 rounded-full h-2" data-id="element-1545">
                <div className="bg-[#00f4f8] h-2 rounded-full" style={{
                width: '37.5%'
              }} data-id="element-1546"></div>
              </div>
            </div>

            {/* Module Content */}
            <div className="p-8" data-id="element-1547">
              <h4 className="text-2xl font-bold text-[#111111] mb-4" data-id="element-1548">
                Step 3: Check the sender's email address
              </h4>
              <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-1549">
                Scammers often use email addresses that look similar to real
                companies but have small differences. Here's how to spot them:
              </p>

              {/* Visual Example */}
              <div className="bg-[#f8f8f8] rounded-xl p-6 mb-6 border-2 border-gray-200" data-id="element-1550">
                <p className="text-sm font-semibold text-gray-500 mb-4" data-id="element-1551">
                  EXAMPLE: Spot the fake
                </p>
                <div className="space-y-3" data-id="element-1552">
                  <div className="flex items-center bg-white rounded-lg p-4 border-2 border-green-200" data-id="element-1553">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-3" data-id="element-1554" />
                    <span className="font-mono text-sm" data-id="element-1555">
                      support@amazon.com
                    </span>
                    <span className="ml-auto text-green-600 text-sm font-medium" data-id="element-1556">
                      Real
                    </span>
                  </div>
                  <div className="flex items-center bg-white rounded-lg p-4 border-2 border-red-200" data-id="element-1557">
                    <span className="text-red-500 mr-3" data-id="element-1558">✗</span>
                    <span className="font-mono text-sm" data-id="element-1559">
                      support@amaz0n-secure.com
                    </span>
                    <span className="ml-auto text-red-600 text-sm font-medium" data-id="element-1560">
                      Fake
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between" data-id="element-1561">
                <button className="text-gray-500 font-medium hover:text-[#631bb7] transition-colors" data-id="element-1562">
                  ← Previous Step
                </button>
                <button className="bg-[#631bb7] text-white px-6 py-3 rounded-lg font-bold hover:bg-[#521099] transition-colors" data-id="element-1563">
                  Next Step →
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="w-full bg-white py-16 md:py-24 border-t border-gray-200" data-id="element-1564">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1565">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1566">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-4" data-id="element-1567">
              Ready to start learning?
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8" data-id="element-1568">
              Take your free assessment and get your personalized learning path
              in under 3 minutes.
            </p>
            <Link to="/intro-assessment" className="bg-[#631bb7] text-white px-8 py-4 rounded-lg font-bold inline-flex items-center justify-center no-underline hover:bg-[#521099] transition-all shadow-md" data-id="element-1569">
              Start My Assessment
            </Link>
          </div>
        </div>
      </section>

      <Footer data-id="element-1570" />
    </div>;
}